import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { NavLink, useNavigate } from 'react-router-dom';
import { Logo } from '@/components/atoms/Logo';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { usePermissions } from '@/hooks/usePermissions';
import { useSidebarBadges } from '@/hooks/useSidebarBadges';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { workspaceAlertCount, type ResolvedWorkspace } from '@/navigation/workspaces';
import type { SidebarBadgeKey, SidebarLinkItem } from '@/navigation/sidebarNav';
import { homePath } from '@/routes/paths';
import { useUIStore } from '@/stores/useUIStore';
import { RealtimeIndicator } from './RealtimeIndicator';
import { TenantSwitcher } from './TenantSwitcher';
import { MOBILE_NAV_DRAWER_ID } from './Topbar';

const SECTION_ID = 'workspace-section';
const badgeSx = { '& .MuiBadge-badge': { fontSize: '0.625rem', minWidth: 16, height: 16 } } as const;

type SidebarView = 'areas' | 'section';

const BADGE_CAPTION: Record<SidebarBadgeKey, string> = {
  openTickets: 'aperti',
  backupPending: 'da mappare',
  unreadNotifications: 'non lette',
  machineAlerts: 'alert',
  preventiviDaInviare: 'da inviare',
};

function linkAria(label: string, key: SidebarBadgeKey | undefined, count: number | undefined): string | undefined {
  if (!key || count == null || count <= 0) return undefined;
  const display = count > 99 ? 'più di 99' : String(count);
  return `${label}, ${display} ${BADGE_CAPTION[key]}`;
}

function areaAria(workspace: ResolvedWorkspace, count: number): string {
  if (count <= 0) return workspace.label;
  const display = count > 99 ? 'più di 99' : String(count);
  return `${workspace.label}, ${display} elementi in evidenza`;
}

interface DestinationProps {
  link: SidebarLinkItem;
  active: boolean;
  touch?: boolean;
  badgeCount?: number;
  onNavigate?: () => void;
}

function Destination({ link, active, touch = false, badgeCount, onNavigate }: DestinationProps): JSX.Element {
  const Icon = link.icon;
  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <ListItemButton
        component={NavLink}
        to={link.to}
        // A shorter sibling such as /admin/documenti must not stay "current"
        // when a longer page under that prefix is the one actually open.
        end={link.exact || !active}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          onNavigate?.();
        }}
        aria-current={active ? 'page' : undefined}
        aria-label={linkAria(link.label, link.badge, badgeCount)}
        sx={{
          minHeight: touch ? 48 : 40,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          color: active ? 'text.primary' : 'text.secondary',
          backgroundColor: active ? 'action.selected' : 'transparent',
          fontWeight: active ? 600 : 500,
          boxShadow: active ? (theme) => `inset 2px 0 0 ${theme.palette.primary.main}` : 'none',
          '&:hover': {
            backgroundColor: 'action.hover',
            color: 'text.primary',
          },
        }}
      >
        {Icon ? (
          <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }} aria-hidden>
            <Icon fontSize="small" />
          </ListItemIcon>
        ) : null}
        <ListItemText
          primary={link.label}
          primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem', fontWeight: 'inherit' }}
        />
        <Badge
          badgeContent={badgeCount}
          color="error"
          invisible={!badgeCount}
          max={99}
          sx={badgeSx}
          componentsProps={{ badge: { 'aria-hidden': true } }}
        />
      </ListItemButton>
    </ListItem>
  );
}

interface AreaListProps {
  workspaces: ResolvedWorkspace[];
  activeId: string | null;
  badges: Partial<Record<SidebarBadgeKey, number>>;
  touch: boolean;
  onOpen: (workspace: ResolvedWorkspace) => void;
}

function AreaList({ workspaces, activeId, badges, touch, onOpen }: AreaListProps): JSX.Element {
  const buttons = useRef<Array<HTMLDivElement | null>>([]);

  const moveFocus = (current: number, direction: 1 | -1): void => {
    const count = workspaces.length;
    if (count === 0) return;
    const next = (current + direction + count) % count;
    buttons.current[next]?.focus();
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      <List disablePadding aria-label="Aree di lavoro" sx={{ px: 1, py: 1, flexShrink: 0 }}>
        {workspaces.map((workspace, index) => {
          const Icon = workspace.icon;
          const count = workspaceAlertCount(workspace.links, badges);
          const selected = workspace.id === activeId;
          return (
            <ListItem key={workspace.id} disablePadding sx={{ display: 'block', mb: 0.5 }}>
              <ListItemButton
                ref={(node) => {
                  buttons.current[index] = node;
                }}
                onClick={() => onOpen(workspace)}
                onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
                  event.preventDefault();
                  moveFocus(index, event.key === 'ArrowDown' ? 1 : -1);
                }}
                aria-label={areaAria(workspace, count)}
                aria-current={selected ? 'true' : undefined}
                sx={{
                  minHeight: touch ? 48 : 44,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  color: selected ? 'primary.main' : 'text.primary',
                  backgroundColor: selected ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }} aria-hidden>
                  <Badge badgeContent={count} color="error" invisible={count <= 0} max={99} sx={badgeSx}>
                    <Icon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={workspace.label}
                  secondary={workspace.description}
                  primaryTypographyProps={{ noWrap: true, fontWeight: 600, fontSize: '0.9375rem' }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRightOutlinedIcon fontSize="small" aria-hidden sx={{ color: 'text.secondary' }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}

interface IconRailProps {
  workspaces: ResolvedWorkspace[];
  activeId: string | null;
  badges: Partial<Record<SidebarBadgeKey, number>>;
  onOpen: (workspace: ResolvedWorkspace) => void;
  onBack: () => void;
}

function IconRail({ workspaces, activeId, badges, onOpen, onBack }: IconRailProps): JSX.Element {
  const { user } = useAuth();
  const buttons = useRef<Array<HTMLDivElement | null>>([]);
  const toggleTheme = useUIStore((state) => state.toggleTheme);
  const themePreference = useUIStore((state) => state.themePreference);

  const moveFocus = (current: number, direction: 1 | -1): void => {
    const count = workspaces.length;
    if (count === 0) return;
    const next = (current + direction + count) % count;
    buttons.current[next]?.focus();
  };

  return (
    <Stack
      sx={(theme) => ({
        width: theme.layout.sidebarRailWidth,
        flexShrink: 0,
        height: '100%',
        minHeight: 0,
        backgroundColor: 'background.rail',
        borderRight: '1px solid',
        borderColor: 'divider',
      })}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, minHeight: 56 }}>
        <Logo compact to={homePath(user?.ruolo)} size="small" />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <List disablePadding aria-label="Aree di lavoro" sx={{ px: 1, py: 1 }}>
          {workspaces.map((workspace, index) => {
            const Icon = workspace.icon;
            const count = workspaceAlertCount(workspace.links, badges);
            const selected = workspace.id === activeId;
            return (
              <ListItem key={workspace.id} disablePadding sx={{ display: 'flex', justifyContent: 'center', mb: 0.75 }}>
                <Tooltip title={workspace.label} placement="right">
                  <ListItemButton
                    ref={(node) => {
                      buttons.current[index] = node;
                    }}
                    onClick={() => onOpen(workspace)}
                    onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
                      event.preventDefault();
                      moveFocus(index, event.key === 'ArrowDown' ? 1 : -1);
                    }}
                    aria-label={areaAria(workspace, count)}
                    aria-current={selected ? 'true' : undefined}
                    aria-controls={SECTION_ID}
                    sx={{
                      width: 44,
                      height: 44,
                      minHeight: 44,
                      flex: '0 0 44px',
                      p: 0,
                      justifyContent: 'center',
                      color: selected ? 'primary.main' : 'text.secondary',
                      backgroundColor: selected ? 'action.selected' : 'transparent',
                      boxShadow: selected ? (theme) => `inset 2px 0 0 ${theme.palette.primary.main}` : 'none',
                      '&:hover': { backgroundColor: 'action.hover', color: selected ? 'primary.main' : 'text.primary' },
                    }}
                  >
                    <Badge badgeContent={count} color="error" invisible={count <= 0} max={99} sx={badgeSx}>
                      <Icon aria-hidden fontSize="small" />
                    </Badge>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Stack spacing={0.5} sx={{ alignItems: 'center', py: 1, flexShrink: 0 }}>
        <Tooltip title={themePreference === 'dark' ? 'Passa al tema chiaro' : 'Passa al tema scuro'}>
          <IconButton
            onClick={toggleTheme}
            aria-label={themePreference === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro'}
            sx={{ width: 44, height: 44 }}
          >
            {themePreference === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <RealtimeIndicator compact />
        <Tooltip title="Torna alle aree">
          <IconButton onClick={onBack} aria-label="Torna all’elenco delle aree" sx={{ width: 44, height: 44 }}>
            <ChevronRightOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

function TenantBar(): JSX.Element {
  return (
    <Box sx={{ px: 2, py: 1.5, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
      <TenantSwitcher />
    </Box>
  );
}

interface SectionNavProps {
  workspace: ResolvedWorkspace | null;
  currentLinkId: string | null;
  badges: Partial<Record<SidebarBadgeKey, number>>;
  touch: boolean;
  showTenant: boolean;
  onBack: () => void;
  onNavigate?: () => void;
}

function SectionNav({ workspace, currentLinkId, badges, touch, showTenant, onBack, onNavigate }: SectionNavProps): JSX.Element {
  return (
    <Stack id={SECTION_ID} component="nav" aria-label={workspace?.label ?? 'Area di lavoro'} sx={{ flex: 1, minWidth: 0, minHeight: 0, height: '100%', backgroundColor: 'background.paper' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1, pt: 1, pb: 0.5 }}>
        <IconButton onClick={onBack} aria-label="Torna alle aree" size="small">
          <ChevronLeftOutlinedIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {workspace?.label ?? 'Aree'}
          </Typography>
          {workspace?.description ? (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {workspace.description}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      {workspace ? (
        <List disablePadding sx={{ px: 1, py: 1, overflowY: 'auto', flex: 1 }}>
          {workspace.subsections.map((subsection) => (
            <ListItem key={subsection.id} disablePadding sx={{ display: 'block', mb: 1 }}>
              <List
                disablePadding
                aria-label={subsection.label}
                subheader={
                  <ListSubheader
                    disableSticky
                    aria-hidden="true"
                    sx={{
                      px: 1.5,
                      pt: 1,
                      pb: 0.5,
                      lineHeight: '24px',
                      bgcolor: 'transparent',
                      color: 'text.secondary',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {subsection.label}
                  </ListSubheader>
                }
              >
                {subsection.links.map((link) => (
                  <Destination
                    key={link.id}
                    link={link}
                    active={link.id === currentLinkId}
                    touch={touch}
                    badgeCount={link.badge ? badges[link.badge] : undefined}
                    onNavigate={onNavigate}
                  />
                ))}
              </List>
            </ListItem>
          ))}
          {workspace.links
            .filter((link) => !workspace.subsections.some((subsection) => subsection.links.some((item) => item.id === link.id)))
            .map((link) => (
              <Destination
                key={link.id}
                link={link}
                active={link.id === currentLinkId}
                touch={touch}
                badgeCount={link.badge ? badges[link.badge] : undefined}
                onNavigate={onNavigate}
              />
            ))}
        </List>
      ) : (
        <Box sx={{ px: 2, py: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Scegli un’area per vedere le pagine.
          </Typography>
        </Box>
      )}

      {showTenant ? <TenantBar /> : null}
    </Stack>
  );
}

interface ChromeProps {
  view: SidebarView;
  workspaces: ResolvedWorkspace[];
  opened: ResolvedWorkspace | null;
  activeId: string | null;
  currentLinkId: string | null;
  badges: Partial<Record<SidebarBadgeKey, number>>;
  touch: boolean;
  showTenant: boolean;
  onOpenArea: (workspace: ResolvedWorkspace) => void;
  onBack: () => void;
  onNavigateLink?: () => void;
  onClose?: () => void;
}

function SidebarChrome({
  view,
  workspaces,
  opened,
  activeId,
  currentLinkId,
  badges,
  touch,
  showTenant,
  onOpenArea,
  onBack,
  onNavigateLink,
  onClose,
}: ChromeProps): JSX.Element {
  const { user } = useAuth();
  const toggleTheme = useUIStore((state) => state.toggleTheme);
  const themePreference = useUIStore((state) => state.themePreference);

  return (
    <Stack sx={{ height: '100%', minHeight: 0, backgroundColor: 'background.paper' }}>
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 56, flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Logo extended to={homePath(user?.ruolo)} />
        </Box>
        {onClose ? (
          <IconButton onClick={onClose} aria-label="Chiudi il menu di navigazione">
            <CloseIcon />
          </IconButton>
        ) : null}
      </Box>

      {view === 'areas' ? (
        <AreaList workspaces={workspaces} activeId={activeId} badges={badges} touch={touch} onOpen={onOpenArea} />
      ) : (
        <SectionNav
          workspace={opened}
          currentLinkId={currentLinkId}
          badges={badges}
          touch={touch}
          showTenant={showTenant}
          onBack={onBack}
          onNavigate={onNavigateLink}
        />
      )}

      {showTenant && view === 'areas' ? <TenantBar /> : null}

      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2, py: 1.5, alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Tooltip title={themePreference === 'dark' ? 'Passa al tema chiaro' : 'Passa al tema scuro'}>
          <IconButton
            onClick={toggleTheme}
            aria-label={themePreference === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro'}
          >
            {themePreference === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <RealtimeIndicator />
      </Stack>
    </Stack>
  );
}

/**
 * Due stati. Di default le aree sono aperte con il nome leggibile e il
 * selettore azienda in quel pannello. Dopo la scelta restano le icone e si
 * apre l’interno (Dashboard: operativa, Panoramica e Report), con il selettore nel
 * pannello aperto. Indietro riporta ai nomi.
 */
export function Sidebar(): JSX.Element {
  const navigate = useNavigate();
  const { canSwitchTenant } = usePermissions();
  const { workspaces, active, activeId, currentLinkId } = useWorkspaces();
  const badges = useSidebarBadges();
  const mobileOpen = useUIStore((state) => state.mobileNavOpen);
  const setMobileNavOpen = useUIStore((state) => state.setMobileNavOpen);
  const { isMobile, isTablet } = useBreakpoint();
  const [view, setView] = useState<SidebarView>('areas');
  const [openedId, setOpenedId] = useState<string | null>(null);

  const opened = workspaces.find((workspace) => workspace.id === (openedId ?? activeId)) ?? active;

  useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile, setMobileNavOpen]);

  const openArea = (workspace: ResolvedWorkspace): void => {
    setOpenedId(workspace.id);
    setView('section');
    if (workspace.id !== activeId && workspace.links[0]) {
      void navigate(workspace.links[0].to);
    }
  };

  return (
    <>
      <Box
        component="aside"
        data-tour="sidebar"
        sx={(theme) => ({
          display: { xs: 'none', sm: 'block' },
          width: theme.layout.sidebarWidth,
          flexShrink: 0,
        })}
      >
        <Box
          sx={(theme) => ({
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: theme.layout.sidebarWidth,
            borderRight: '1px solid',
            borderColor: 'divider',
            zIndex: theme.zIndex.drawer,
            pt: 'env(safe-area-inset-top, 0px)',
            pb: 'env(safe-area-inset-bottom, 0px)',
          })}
        >
          {!isMobile && view === 'areas' ? (
            <SidebarChrome
              view={view}
              workspaces={workspaces}
              opened={opened}
              activeId={activeId}
              currentLinkId={currentLinkId}
              badges={badges}
              touch={isTablet}
              showTenant={canSwitchTenant}
              onOpenArea={openArea}
              onBack={() => setView('areas')}
            />
          ) : null}
          {!isMobile && view === 'section' ? (
            <Stack direction="row" sx={{ height: '100%', minHeight: 0 }}>
              <IconRail
                workspaces={workspaces}
                activeId={opened?.id ?? activeId}
                badges={badges}
                onOpen={openArea}
                onBack={() => setView('areas')}
              />
              <SectionNav
                workspace={opened}
                currentLinkId={currentLinkId}
                badges={badges}
                touch={isTablet}
                showTenant={canSwitchTenant}
                onBack={() => setView('areas')}
              />
            </Stack>
          ) : null}
        </Box>
      </Box>

      <Drawer
        open={mobileOpen}
        onClose={() => setMobileNavOpen(false)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        PaperProps={{ id: MOBILE_NAV_DRAWER_ID }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: 'min(320px, 88vw)',
            backgroundColor: 'background.paper',
            backgroundImage: 'none',
            pt: 'env(safe-area-inset-top, 0px)',
            pb: 'env(safe-area-inset-bottom, 0px)',
            pl: 'env(safe-area-inset-left, 0px)',
          },
        }}
      >
        {isMobile ? (
          <SidebarChrome
            view={view}
            workspaces={workspaces}
            opened={opened}
            activeId={activeId}
            currentLinkId={currentLinkId}
            badges={badges}
            touch
            showTenant={canSwitchTenant}
            onOpenArea={openArea}
            onBack={() => setView('areas')}
            onNavigateLink={() => setMobileNavOpen(false)}
            onClose={() => setMobileNavOpen(false)}
          />
        ) : null}
      </Drawer>
    </>
  );
}
